/**
 * API Client Utilities with React Query Integration
 * Provides typed API functions for use with React Query
 */


import { getCachedCustomer, cacheCustomer } from "../offline/customer-cache";
import { fetchWithTimeout } from "./fetch-with-timeout";

/**
 * Search customer by barcode or phone
 */
export async function searchCustomer(
  query: string,
  deviceToken?: string
): Promise<{ found: boolean; customer?: any; message?: string }> {
  // Check cache first (for barcodes only)
  if (query.startsWith("R") && query.length === 13) {
    const cached = await getCachedCustomer(query);
    if (cached) {
      return { found: true, customer: cached };
    }
  }

  // Fetch from API
  const response = await fetchWithTimeout(`/api/customers/search?query=${encodeURIComponent(query)}`, {
    headers: {
      Accept: "application/json",
      ...(deviceToken && { "X-Device-Token": deviceToken }),
    },
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      found: false,
      message: data.error || "Customer not found",
    };
  }

  // Cache the result if found
  if (data.found && data.customer && query.startsWith("R")) {
    await cacheCustomer(query, data.customer);
  }

  return data;
}

/**
 * Earn points transaction
 */
export async function earnPoints(params: {
  barcode: string;
  amount: number;
  cashier_name: string;
  deviceToken: string;
  notes?: string;
}): Promise<{
  success: boolean;
  transaction?: any;
  error?: string;
}> {
  const idempotencyKey = crypto.randomUUID();

  const response = await fetchWithTimeout("/api/transactions/earn", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Device-Token": params.deviceToken,
    },
    body: JSON.stringify({
      barcode: params.barcode,
      amount: params.amount,
      cashier_name: params.cashier_name,
      idempotency_key: idempotencyKey,
      notes: params.notes,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || "Failed to earn points",
    };
  }

  return {
    success: true,
    transaction: data.transaction,
  };
}

/**
 * Redeem points transaction
 */
export async function redeemPoints(params: {
  barcode: string;
  points_to_redeem: number;
  cashier_name: string;
  deviceToken: string;
  notes?: string;
}): Promise<{
  success: boolean;
  transaction?: any;
  error?: string;
}> {
  const idempotencyKey = crypto.randomUUID();

  const response = await fetchWithTimeout("/api/transactions/redeem", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Device-Token": params.deviceToken,
    },
    body: JSON.stringify({
      barcode: params.barcode,
      points_to_redeem: params.points_to_redeem,
      cashier_name: params.cashier_name,
      idempotency_key: idempotencyKey,
      notes: params.notes,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || "Failed to redeem points",
    };
  }

  return {
    success: true,
    transaction: data.transaction,
  };
}

/**
 * Redeem points by customer ID (for customer detail page)
 */
export async function redeemPointsByCustomerId(params: {
  customerId: number;
  pointsToRedeem: number;
  notes?: string;
  redemptionType?: "direct" | "return";
}): Promise<{
  success: boolean;
  transaction?: any;
  error?: string;
}> {
  const endpoint =
    params.redemptionType === "return"
      ? "/api/transactions/redeem-return"
      : "/api/transactions/redeem-direct";

  const response = await fetchWithTimeout(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      customerId: params.customerId,
      pointsToRedeem: params.pointsToRedeem,
      notes: params.notes?.trim() || null,
      redemptionType: params.redemptionType || "direct",
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || "Failed to redeem points",
    };
  }

  return {
    success: true,
    transaction: data,
  };
}

/**
 * Create new customer
 */
export async function createCustomer(params: {
  barcode: string;
  name: string;
  phone: string;
  date_of_birth?: string;
  deviceToken: string;
}): Promise<{
  success: boolean;
  customer?: any;
  error?: string;
}> {
  const response = await fetchWithTimeout("/api/customers/create", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Device-Token": params.deviceToken,
    },
    body: JSON.stringify({
      barcode: params.barcode,
      name: params.name,
      phone: params.phone,
      date_of_birth: params.date_of_birth,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || "Failed to create customer",
    };
  }

  return {
    success: true,
    customer: data.customer,
  };
}

/**
 * Admin: read system_config. Requires admin password via header.
 * Used by the new /api/admin/settings endpoint (Task A).
 */
export async function getSystemConfig(adminPassword: string): Promise<{
  success: boolean;
  config?: Array<{ key: string; value: string; description: string | null }>;
  error?: string;
}> {
  const response = await fetchWithTimeout("/api/admin/settings", {
    method: "GET",
    headers: {
      Accept: "application/json",
      "X-Admin-Password": adminPassword,
    },
  });
  const data = await response.json();
  if (!response.ok) {
    return { success: false, error: data.error || "Failed to load settings" };
  }
  return { success: true, config: data.config };
}

/**
 * Admin: update one or more system_config keys. Sends an idempotency key so
 * that a retry after a network blip will not double-apply (POKA-YOKE 6 on
 * the server). The client's TanStack Query cache is invalidated by the
 * caller (see SystemConfigEditor.onSuccess).
 */
export async function updateSystemConfig(
  updates: Array<{ key: string; value: string }>,
  adminPassword: string,
): Promise<{
  success: boolean;
  config?: Array<{ key: string; value: string; description: string | null }>;
  error?: string;
}> {
  const idempotencyKey = crypto.randomUUID();
  const response = await fetchWithTimeout("/api/admin/settings", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Password": adminPassword,
    },
    body: JSON.stringify({ updates, idempotency_key: idempotencyKey }),
  });
  const data = await response.json();
  if (!response.ok) {
    return {
      success: false,
      error: data.error || "Failed to update settings",
    };
  }
  return { success: true, config: data.config };
}

/**
 * Get customer by ID
 */
export async function getCustomerById(id: string | number): Promise<{
  success: boolean;
  customer?: any;
  recentTransactions?: any[];
  error?: string;
}> {
  const response = await fetchWithTimeout(`/api/customers/get?id=${id}`);
  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || "Failed to fetch customer",
    };
  }

  return {
    success: true,
    customer: data.customer,
    recentTransactions: data.recentTransactions,
  };
}
